import type {
  Alarm,
  ControllerType,
  DashboardSummary,
  InviteResponse,
  Machine,
  ShopUserResponse,
  Telemetry,
  ToolCheckoutRecord,
  ToolGraveyardRecord,
  ToolHolderRecord,
  ToolLibraryRecord,
  ToolListResponse,
  ToolPocketRuleRecord,
  ToolSlotResponse,
  UserRole,
  ValidateProgramResponse,
} from '@bridgewerk/shared';

export interface ToolAssignment {
  readonly machine_id: string;
  readonly tool_number: number;
  readonly life_percent: number;
  readonly status: 'change' | 'order' | 'watch' | 'good';
  readonly assigned_at: string | null;
  readonly last_changed_at: string | null;
  readonly updated_at: string;
}

export interface CreateToolHolderInput {
  readonly description:        string;
  readonly type:               ToolHolderRecord['type'];
  readonly material:           ToolHolderRecord['material'];
  readonly manufacturer?:      string | null;
  readonly part_number?:       string | null;
  readonly diameter_mm?:       number | null;
  readonly length_mm?:         number | null;
  readonly expected_life_cuts?: number | null;
}

export interface CheckoutToolInput {
  readonly holder_id:       string;
  readonly from_machine_id: string;
  readonly from_slot:       number;
  readonly checked_out_by:  string;
}

export interface CheckinToolInput {
  readonly holder_id:        string;
  readonly geometry_changed: boolean;
  readonly new_length_mm?:   number | null;
  readonly notes?:           string | null;
}

export interface SetPocketRuleInput {
  readonly allowed_types:       ToolHolderRecord['type'][];
  readonly max_diameter_mm?:    number | null;
  readonly designation:         'dedicated' | 'flexible';
  readonly dedicated_holder_id?: string | null;
}

export type EnrichedCheckout = ToolCheckoutRecord & { holder_description: string | null };

export interface CreateToolLibraryInput {
  readonly description:           string;
  readonly type:                  ToolLibraryRecord['type'];
  readonly material:              ToolLibraryRecord['material'];
  readonly manufacturer?:         string | null;
  readonly part_number?:          string | null;
  readonly diameter_mm?:          number | null;
  readonly expected_life_cuts?:   number | null;
  readonly expected_life_minutes?: number | null;
  readonly notes?:                string | null;
}

// ── Team / user types ────────────────────────────────────────────────────────

export interface InviteInput {
  readonly email:       string;
  readonly role:        UserRole;
  readonly machine_ids?: string[];
  readonly message?:    string;
}

export interface TeamUserPatch {
  readonly role?:         UserRole;
  readonly is_active?:    boolean;
  readonly display_name?: string | null;
}

export interface AssignmentInput {
  readonly user_id:      string;
  readonly role_context: UserRole;
  readonly is_primary?:  boolean;
  readonly shift?:       'day' | 'evening' | 'night' | 'all' | null;
  readonly notes?:       string | null;
}

export interface BulkAssignmentItem {
  readonly user_id:      string;
  readonly role_context: UserRole;
  readonly is_primary?:  boolean;
  readonly shift?:       'day' | 'evening' | 'night' | 'all' | null;
}

export interface MachineAssignmentsResponse {
  readonly machine_id: string;
  readonly assigned_users: {
    readonly operators:   AssignedUserEntry[];
    readonly programmers: AssignedUserEntry[];
  };
}

export interface AssignedUserEntry {
  readonly user_id:      string;
  readonly email:        string;
  readonly display_name: string | null;
  readonly role_context: UserRole;
  readonly is_primary:   boolean;
  readonly shift:        string | null;
  readonly avatar_url:   string | null;
}

export type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'NETWORK'
  | 'PARSE'
  | 'INTERNAL';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: ApiErrorCode;
  public readonly body: unknown;

  public constructor(status: number, code: ApiErrorCode, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  public get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export interface TelemetryQuery {
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
}

export type FetchLike = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** Returns the current Cognito access token, or null if not signed in. */
  readonly getToken?: () => Promise<string | null> | string | null;
  /** Called whenever the API returns 401, before the error is thrown. */
  readonly onUnauthorized?: () => void;
  /** Test hook. Defaults to global fetch. */
  readonly fetch?: FetchLike;
}

export class ApiClient {
  private readonly fetchImpl: FetchLike;

  public constructor(private readonly opts: ApiClientOptions) {
    this.fetchImpl = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  public async getDashboardStats(shopId: string): Promise<DashboardSummary> {
    return this.request<DashboardSummary>(`/shops/${encode(shopId)}/dashboard`);
  }

  public async getMachines(shopId: string): Promise<Machine[]> {
    const res = await this.request<{ machines: Machine[] }>(
      `/shops/${encode(shopId)}/machines`,
    );
    return res.machines;
  }

  public async getMachine(shopId: string, machineId: string): Promise<Machine> {
    const res = await this.request<{ machine: Machine }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}`,
    );
    return res.machine;
  }

  public async createMachine(shopId: string, machineId: string, controllerType: ControllerType): Promise<Machine> {
    const res = await this.request<{ machine: Machine }>(
      `/shops/${encode(shopId)}/machines`,
      { method: 'POST', body: JSON.stringify({ machine_id: machineId, controller_type: controllerType }) },
    );
    return res.machine;
  }

  public async deleteMachine(shopId: string, machineId: string): Promise<void> {
    await this.request<void>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}`,
      { method: 'DELETE' },
    );
  }

  public async getMachineTelemetry(
    shopId: string,
    machineId: string,
    query: TelemetryQuery = {},
  ): Promise<Telemetry[]> {
    const qs = buildQuery(query);
    const res = await this.request<{ points: Telemetry[] }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/telemetry${qs}`,
    );
    return res.points;
  }

  public async getMachineAlarms(shopId: string, machineId: string): Promise<Alarm[]> {
    const res = await this.request<{ alarms: Alarm[] }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/alarms`,
    );
    return res.alarms;
  }

  public async getMachineTools(shopId: string, machineId: string): Promise<ToolListResponse> {
    return this.request<ToolListResponse>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/tools`,
    );
  }

  public async getToolAssignments(shopId: string, toolId: string): Promise<ToolAssignment[]> {
    const res = await this.request<{ assignments: ToolAssignment[] }>(
      `/shops/${encode(shopId)}/toollibrary/${encode(toolId)}/assignments`,
    );
    return res.assignments;
  }

  public async getToolLibrary(shopId: string): Promise<ToolLibraryRecord[]> {
    const res = await this.request<{ tools: ToolLibraryRecord[]; count: number }>(
      `/shops/${encode(shopId)}/toollibrary`,
    );
    return res.tools;
  }

  public async createToolLibraryEntry(
    shopId: string,
    input: CreateToolLibraryInput,
  ): Promise<ToolLibraryRecord> {
    return this.request<ToolLibraryRecord>(
      `/shops/${encode(shopId)}/toollibrary`,
      { method: 'POST', body: input },
    );
  }

  public async updateToolLibraryEntry(
    shopId: string,
    toolId: string,
    input: CreateToolLibraryInput,
  ): Promise<ToolLibraryRecord> {
    return this.request<ToolLibraryRecord>(
      `/shops/${encode(shopId)}/toollibrary/${encode(toolId)}`,
      { method: 'PUT', body: input },
    );
  }

  public async getGraveyard(shopId: string): Promise<ToolGraveyardRecord[]> {
    const res = await this.request<{ entries: ToolGraveyardRecord[]; count: number }>(
      `/shops/${encode(shopId)}/toollibrary/graveyard`,
    );
    return res.entries;
  }

  public async deleteToolLibraryEntry(shopId: string, toolId: string): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/toollibrary/${encode(toolId)}`,
      { method: 'DELETE' },
    );
  }

  public async assignToolToSlot(
    shopId: string,
    machineId: string,
    toolNumber: number,
    toolLibraryId: string,
  ): Promise<ToolSlotResponse> {
    return this.request<ToolSlotResponse>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/tools/${toolNumber}/assign`,
      { method: 'POST', body: { tool_library_id: toolLibraryId } },
    );
  }

  public async unassignToolFromSlot(
    shopId: string,
    machineId: string,
    toolNumber: number,
  ): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/tools/${toolNumber}/assign`,
      { method: 'DELETE' },
    );
  }

  // ── Custody ────────────────────────────────────────────────────────────────

  public async listToolHolders(shopId: string): Promise<ToolHolderRecord[]> {
    const res = await this.request<{ holders: ToolHolderRecord[]; count: number }>(
      `/shops/${encode(shopId)}/tools/holders`,
    );
    return res.holders;
  }

  public async createToolHolder(shopId: string, input: CreateToolHolderInput): Promise<ToolHolderRecord> {
    return this.request<ToolHolderRecord>(
      `/shops/${encode(shopId)}/tools/holders`,
      { method: 'POST', body: input },
    );
  }

  public async getOutstandingCheckouts(shopId: string): Promise<EnrichedCheckout[]> {
    const res = await this.request<{ checkouts: EnrichedCheckout[]; count: number }>(
      `/shops/${encode(shopId)}/tools/outstanding`,
    );
    return res.checkouts;
  }

  public async checkoutTool(shopId: string, input: CheckoutToolInput): Promise<ToolCheckoutRecord> {
    return this.request<ToolCheckoutRecord>(
      `/shops/${encode(shopId)}/tools/checkout`,
      { method: 'POST', body: input },
    );
  }

  public async checkinTool(shopId: string, input: CheckinToolInput): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/tools/checkin`,
      { method: 'POST', body: input },
    );
  }

  public async getPocketRules(shopId: string, machineId: string): Promise<ToolPocketRuleRecord[]> {
    const res = await this.request<{ rules: ToolPocketRuleRecord[]; count: number }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/pocketrules`,
    );
    return res.rules;
  }

  public async setPocketRule(
    shopId: string,
    machineId: string,
    slot: number,
    input: SetPocketRuleInput,
  ): Promise<ToolPocketRuleRecord> {
    return this.request<ToolPocketRuleRecord>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/pocketrules/${slot}`,
      { method: 'PUT', body: input },
    );
  }

  public async validateProgram(
    shopId: string,
    machineId: string,
    programGcode: string,
  ): Promise<ValidateProgramResponse> {
    return this.request<ValidateProgramResponse>(
      `/shops/${encode(shopId)}/tools/validate-program`,
      { method: 'POST', body: { machine_id: machineId, program_gcode: programGcode } },
    );
  }

  // ── Team management ─────────────────────────────────────────────────────────

  public async getMe(shopId: string): Promise<ShopUserResponse> {
    return this.request<ShopUserResponse>(`/shops/${encode(shopId)}/me`);
  }

  public async listTeamUsers(shopId: string): Promise<{
    users: ShopUserResponse[];
    invites: InviteResponse[];
    count: number;
  }> {
    return this.request(`/shops/${encode(shopId)}/users`);
  }

  public async inviteUser(shopId: string, input: InviteInput): Promise<{
    invite_id: string;
    email: string;
    role: UserRole;
    expires_at: string;
  }> {
    return this.request(`/shops/${encode(shopId)}/users/invite`, {
      method: 'POST',
      body: input,
    });
  }

  public async getInvite(shopId: string, inviteId: string): Promise<InviteResponse> {
    return this.request<InviteResponse>(
      `/shops/${encode(shopId)}/users/invite/${encode(inviteId)}`,
    );
  }

  public async acceptInvite(shopId: string, inviteId: string): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/users/invite/${encode(inviteId)}/accept`,
      { method: 'POST' },
    );
  }

  public async updateTeamUser(
    shopId: string,
    userId: string,
    patch: TeamUserPatch,
  ): Promise<ShopUserResponse> {
    return this.request<ShopUserResponse>(
      `/shops/${encode(shopId)}/users/${encode(userId)}`,
      { method: 'PATCH', body: patch },
    );
  }

  public async removeTeamUser(shopId: string, userId: string): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/users/${encode(userId)}`,
      { method: 'DELETE' },
    );
  }

  public async listMachineAssignments(
    shopId: string,
    machineId: string,
  ): Promise<MachineAssignmentsResponse> {
    return this.request<MachineAssignmentsResponse>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/assignments`,
    );
  }

  public async createAssignment(
    shopId: string,
    machineId: string,
    input: AssignmentInput,
  ): Promise<AssignedUserEntry & { assigned_by: string; assigned_at: string; notes: string | null }> {
    return this.request(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/assignments`,
      { method: 'POST', body: input },
    );
  }

  public async deleteAssignment(
    shopId: string,
    machineId: string,
    userId: string,
  ): Promise<void> {
    await this.request<{ ok: boolean }>(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/assignments/${encode(userId)}`,
      { method: 'DELETE' },
    );
  }

  public async bulkReplaceAssignments(
    shopId: string,
    machineId: string,
    assignments: BulkAssignmentItem[],
  ): Promise<{ assignments: unknown[]; count: number }> {
    return this.request(
      `/shops/${encode(shopId)}/machines/${encode(machineId)}/assignments/bulk`,
      { method: 'POST', body: { assignments } },
    );
  }

  /**
   * Submit an audio blob (MediaRecorder output) for transcription + intent
   * resolution. Server transcribes via Whisper and returns the parsed tool
   * call; the dashboard executes it after operator confirmation.
   */
  public async resolveTextIntent(transcript: string, context?: string): Promise<{
    transcript: string;
    tool: string;
    args: Record<string, unknown>;
  }> {
    return this.request<{ transcript: string; tool: string; args: Record<string, unknown> }>(
      '/voice/intent',
      { method: 'POST', body: { transcript, context } },
    );
  }

  public async resolveVoiceIntent(audio: Blob, context?: string): Promise<{
    transcript: string;
    tool: string;
    args: Record<string, unknown>;
  }> {
    const url = `${this.opts.baseUrl}/voice/intent`;
    const headers: Record<string, string> = {};
    const token = await this.resolveToken();
    if (token) headers['authorization'] = `Bearer ${token}`;

    const form = new FormData();
    const filename = audio.type.includes('mp4') ? 'clip.m4a' : 'clip.webm';
    form.append('audio', audio, filename);
    if (context) form.append('context', context);

    let res: Response;
    try {
      res = await this.fetchImpl(url, { method: 'POST', headers, body: form });
    } catch (err) {
      throw new ApiError(0, 'NETWORK', `network error: ${stringifyError(err)}`);
    }
    if (res.status === 401) this.opts.onUnauthorized?.();
    if (!res.ok) {
      const body = await readBodySafe(res);
      throw new ApiError(
        res.status,
        mapStatusToCode(res.status),
        extractErrorMessage(body) ?? `voice request failed: ${res.status}`,
        body,
      );
    }
    return res.json() as Promise<{ transcript: string; tool: string; args: Record<string, unknown> }>;
  }

  private async request<T>(
    path: string,
    init: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const url = `${this.opts.baseUrl}${path}`;
    const headers: Record<string, string> = { accept: 'application/json' };
    const token = await this.resolveToken();
    if (token) headers['authorization'] = `Bearer ${token}`;

    const fetchInit: RequestInit = { headers, method: init.method ?? 'GET' };
    if (init.body !== undefined) {
      headers['content-type'] = 'application/json';
      fetchInit.body = JSON.stringify(init.body);
    }

    let res: Response;
    try {
      res = await this.fetchImpl(url, fetchInit);
    } catch (err) {
      throw new ApiError(0, 'NETWORK', `network error: ${stringifyError(err)}`);
    }

    if (res.status === 401) {
      this.opts.onUnauthorized?.();
    }

    if (!res.ok) {
      const body = await readBodySafe(res);
      const code = mapStatusToCode(res.status);
      const message = extractErrorMessage(body) ?? `request failed: ${res.status}`;
      throw new ApiError(res.status, code, message, body);
    }

    try {
      return (await res.json()) as T;
    } catch (err) {
      throw new ApiError(res.status, 'PARSE', `failed to parse response: ${stringifyError(err)}`);
    }
  }

  private async resolveToken(): Promise<string | null> {
    const value = this.opts.getToken?.();
    if (value == null) return null;
    return value instanceof Promise ? await value : value;
  }
}

function encode(part: string): string {
  return encodeURIComponent(part);
}

function buildQuery(q: TelemetryQuery): string {
  const params = new URLSearchParams();
  if (q.from) params.set('from', q.from);
  if (q.to) params.set('to', q.to);
  if (q.limit != null) params.set('limit', String(q.limit));
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function readBodySafe(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '');
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function mapStatusToCode(status: number): ApiErrorCode {
  if (status === 400) return 'BAD_REQUEST';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  return 'INTERNAL';
}

function extractErrorMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const obj = body as Record<string, unknown>;
  if (typeof obj.message === 'string') return obj.message;
  if (typeof obj.error === 'string') return obj.error;
  return undefined;
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

let defaultClient: ApiClient | undefined;

export function configureApiClient(opts: ApiClientOptions): ApiClient {
  defaultClient = new ApiClient(opts);
  return defaultClient;
}

export function getApiClient(): ApiClient {
  if (!defaultClient) {
    defaultClient = new ApiClient({
      baseUrl: import.meta.env.VITE_API_URL ?? '',
    });
  }
  return defaultClient;
}
